import { Button } from '@/components/ui/button'
import type { CreateSetup, Setup } from '@hvf/contracts'
import {
  createForm,
  getValues,
  reset,
  setValue,
  setValues,
  valiForm,
} from '@modular-forms/solid'
import { userId } from '@stateApp'
import dayjs from 'dayjs'
import { isEqual } from 'lodash-es'
import { createEffect, onCleanup } from 'solid-js'
import { useService } from 'solid-services'
import { css } from 'styled-system/css'
import { Flex, Grid } from 'styled-system/jsx'
import { useCreate } from '../state'
import ImageCropAndOCR from './ImageDetection'
import type { ScanedDetails } from './ImageDetection/scannedDetails'
import { CreateSetupSchema } from './schema/createSetupSchema'
import { updateComputedFields } from './updateComputedFields'
import Computed from './views/Computed'
import General from './views/General'
import Technical from './views/Technical'

export type FormFieldType = ReturnType<
  typeof createForm<CreateSetup | Setup>
>[1]['Field']

export default function Details() {
  const { completeDetailStep, newSetup, setNewSetup, isEditing, saveDraft, setupMutation } =
    useService(useCreate)()
  const [form, { Form, Field: FormField }] = createForm<CreateSetup | Setup>({
    validate: valiForm(CreateSetupSchema),
    initialValues: {
      created_by: userId(),
      id: undefined,
      title: '',
      symbol: '',
      exchange: '',
      description: '',
      pattern_specification: undefined,
      image_macro: '',
      image_pattern: '',
      image_time_stop: '',
      image_trend: '',
      image_trigger: '',
      image_volume_by_price: '',
      first_in_new_trend: false,
      has_primer: false,
      high_1_price: undefined,
      high_1_time: '',
      scan_tool_valid: null,
    },
  })

  createEffect(() => {
    const initialValues = newSetup()

    if (initialValues) {
      setValues(form, initialValues as any)
    } else {
      reset(form)
    }
  })

  onCleanup(() => {
    isEditing() ? setNewSetup(undefined) : setNewSetup(getValues(form) as any)
  })

  createEffect(() => {
    if (!isEditing()) {
      setValue(form, 'created_by', userId() || '')
    }
  })

  // this function calculates the computed fields, and updates the form
  createEffect(() => {
    const formValues = getValues(form)
    const currentFormValue = newSetup()

    if (!isEqual(formValues, currentFormValue) && !form.submitted) {
      updateComputedFields(form)
      // IMPORTANT: Fetch getValues(form) AGAIN so we save the newly computed fields!
      setNewSetup(getValues(form) as any)
    }
  })

  const onConfirmDetails = (details: ScanedDetails, callback: () => void) => {
    const position = details.high_1_time! < details.low_1_time! ? 'long' : 'short'

    if (details.high_1_time) {
      setValue(form, 'high_1_time', dayjs.unix(details.high_1_time).format('YYYY-MM-DD'))
    }
    if (details.high_2_time) {
      setValue(form, 'high_2_time', dayjs.unix(details.high_2_time).format('YYYY-MM-DD'))
    }
    if (details.high_3_time) {
      setValue(form, 'high_3_time', dayjs.unix(details.high_3_time).format('YYYY-MM-DD'))
    }
    if (details.low_1_time) {
      setValue(form, 'low_1_time', dayjs.unix(details.low_1_time).format('YYYY-MM-DD'))
    }
    if (details.low_2_time) {
      setValue(form, 'low_2_time', dayjs.unix(details.low_2_time).format('YYYY-MM-DD'))
    }
    if (details.low_3_time) {
      setValue(form, 'low_3_time', dayjs.unix(details.low_3_time).format('YYYY-MM-DD'))
    }

    setValues(form, {
      title: details.symbol,
      scan_tool_valid: true,
      position,
      symbol: details.symbol,
      exchange: details.exchange,
      high_1_price: details.high_1_price,
      high_2_price: details.high_2_price,
      high_3_price: details.high_3_price,
      low_1_price: details.low_1_price,
      low_2_price: details.low_2_price,
      low_3_price: details.low_3_price,
    })

    callback()
  }

  return (
    <Grid gap={6} mt={3}>
      <ImageCropAndOCR onConfirmDetails={onConfirmDetails} />
      <Form
        class={css({
          display: 'grid',
          gap: '6',
        })}
        onSubmit={(v) => {
          completeDetailStep(v)
        }}
      >
        <General
          FormField={FormField}
          form={form}
        />
        <Technical
          FormField={FormField}
          form={form}
        />
        <Computed
          FormField={FormField}
          form={form}
        />
        <Grid gridTemplateColumns='1fr 1fr'>
          {/* // add in function handler here */}
          <Button
            variant='outline'
            type='button'
            loading={setupMutation.isPending}
            onClick={() => saveDraft(getValues(form) as any)}
          >
            Save Draft
          </Button>
          <Button type="submit">Confirm Setup</Button>
        </Grid>
      </Form>
    </Grid>
  )
}
